import { createContext, useCallback, useContext, useRef, useState } from 'react';

const UIContext = createContext(null);

export const useUI = () => {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used within UIProvider');
  return ctx;
};

let toastId = 0;
const STATUS_ICON_TOKENS = new Set(['✅', '✔', '☑', 'ℹ', '⚠', '❌', '✖', '✕', '⬇']);

function normalizeToastMessage(message) {
  const text = String(message ?? '').trim();
  if (!text) return '';

  const [firstToken, ...restTokens] = text.split(/\s+/);
  const normalizedToken = firstToken.replace(/\uFE0F/g, '');
  if (STATUS_ICON_TOKENS.has(normalizedToken) && restTokens.length > 0) {
    return restTokens.join(' ').trim();
  }
  return text;
}

export const UIProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});
  const activeKeys = useRef(new Set());

  const removeToast = useCallback((id) => {
    setToasts((prev) => {
      const toast = prev.find((t) => t.id === id);
      if (toast) activeKeys.current.delete(`${toast.type}::${toast.message}`);
      return prev.filter((t) => t.id !== id);
    });
    clearTimeout(timers.current[id]);
    delete timers.current[id];
  }, []);

  /**
   * Show a toast notification.
   * Deduplicates by message+type — if an identical toast is already visible it is skipped.
   * @param {string} message
   * @param {'info'|'success'|'error'|'warning'} [type='info']
   * @param {number} [duration=3500]
   */
  const showToast = useCallback((message, type = 'info', duration = 3500) => {
    const normalizedMessage = normalizeToastMessage(message);
    if (!normalizedMessage) return;

    const key = `${type}::${normalizedMessage}`;
    if (activeKeys.current.has(key)) return;

    activeKeys.current.add(key);
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message: normalizedMessage, type }]);
    timers.current[id] = setTimeout(() => removeToast(id), duration);
  }, [removeToast]);

  return (
    <UIContext.Provider value={{ toasts, showToast, removeToast }}>
      {children}
    </UIContext.Provider>
  );
};

export default UIContext;
